import {
  useCategories,
  useCreateMenuItem,
  useDeleteMenuItem,
  useDepartments,
  useMenuItemsPaginated,
  useUpdateMenuItem,
} from '@hooks/menu';
import { useAppSelector } from '@hooks/useRedux';
import { selectUserCurrencyPrefix } from '@store/auth/authSelectors';
import { selectEffectiveOutlet, selectEffectiveOutletId } from '@store/outlets';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { CreateMenuItemRequest, MenuItem } from '../../../types/menu';
import { LoadingState } from '../../common/LoadingState';
import { PaginationControls } from '../../common/PaginationControls';
import { DeleteConfirmModal } from '../departments/DeleteConfirmModal';
import { MenuEmptyState, MenuItemsGrid, MenuItemsTable } from './components';
import { ErrorState } from './ErrorState';
import { MenuItemFormModal } from './MenuItemFormModal';

export interface MenuItemsRef {
  openCreateModal: () => void;
}

interface MenuItemsProps {
  canManageMenu?: boolean;
  viewMode: 'grid' | 'list';
  categoryFilter: string | null;
  searchQuery?: string;
  page: number;
  onPageChange: (page: number) => void;
  onImportWithAI?: () => void;
}

export const MenuItems = forwardRef<MenuItemsRef, MenuItemsProps>(
  (
    {
      canManageMenu = true,
      viewMode,
      categoryFilter,
      searchQuery = '',
      page,
      onPageChange,
      onImportWithAI,
    },
    ref,
  ) => {
    const outletId = useAppSelector(selectEffectiveOutletId);
    const selectedOutlet = useAppSelector(selectEffectiveOutlet);
    const currencyPrefix = useAppSelector(selectUserCurrencyPrefix);

    const {
      data: pagedData,
      isLoading,
      isError,
      refetch,
    } = useMenuItemsPaginated({
      outletId: outletId ?? '',
      search: searchQuery || undefined,
      categoryId: categoryFilter || undefined,
      page,
      pageSize: 20,
    });

    const items = pagedData?.data ?? [];
    const pagination = pagedData?.pagination;

    const { data: allCategories = [] } = useCategories(outletId);
    const categories = allCategories.filter((c) => c.status === 'active');
    const { data: allDepartments = [] } = useDepartments(outletId);
    const departments = allDepartments.filter((d) => d.status === 'active');

    const createMenuItem = useCreateMenuItem();
    const updateMenuItem = useUpdateMenuItem();
    const deleteMenuItem = useDeleteMenuItem();
    const isSubmitting =
      createMenuItem.isPending ||
      updateMenuItem.isPending ||
      deleteMenuItem.isPending;

    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);

    useImperativeHandle(ref, () => ({
      openCreateModal: () => {
        if (!canManageMenu) return;
        setSelectedItem(null);
        setIsItemModalOpen(true);
      },
    }));

    const handleCreateItem = (data: CreateMenuItemRequest) => {
      if (!outletId || !canManageMenu) return;
      createMenuItem.mutate(
        { ...data, outlet_id: outletId },
        {
          onSuccess: () => {
            // Keep the modal open so the user can create the next item.
            // The modal resets item-specific fields (name, price, sku) but
            // preserves category/department/status from the previous save.
            setSelectedItem(null);
          },
        },
      );
    };

    const handleUpdateItem = (data: CreateMenuItemRequest) => {
      if (!selectedItem || !canManageMenu) return;
      updateMenuItem.mutate(
        { id: selectedItem.id, ...data },
        {
          onSuccess: () => {
            setIsItemModalOpen(false);
            setSelectedItem(null);
          },
        },
      );
    };

    const handleDeleteConfirm = () => {
      if (selectedItem && canManageMenu) {
        deleteMenuItem.mutate(selectedItem.id, {
          onSuccess: () => setSelectedItem(null),
        });
      }
      setIsDeleteModalOpen(false);
    };

    const openEditItem = (item: MenuItem) => {
      if (!canManageMenu) return;
      setSelectedItem(item);
      setIsItemModalOpen(true);
    };

    const openDeleteItem = (item: MenuItem) => {
      if (!canManageMenu) return;
      setSelectedItem(item);
      setIsDeleteModalOpen(true);
    };

    const openCreateModal = () => {
      if (!outletId || !canManageMenu) return;
      setSelectedItem(null);
      setIsItemModalOpen(true);
    };

    const formatPrice = (price: number) =>
      `${currencyPrefix} ${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(price)}`;

    const renderContent = () => {
      if (isLoading && items.length === 0) {
        return <LoadingState message="Loading menu items..." size="lg" />;
      }

      if (isError) {
        return (
          <ErrorState
            title="Failed to load menu items"
            description="There was a problem connecting to the server. Please check your internet connection and try again."
            onPress={refetch}
          />
        );
      }

      if (items.length === 0) {
        return (
          <MenuEmptyState
            canManageMenu={canManageMenu}
            searchQuery={searchQuery}
            onCreateItem={canManageMenu ? openCreateModal : undefined}
            onImportWithAI={canManageMenu ? onImportWithAI : undefined}
          />
        );
      }

      if (viewMode === 'grid') {
        return (
          <MenuItemsGrid
            canManageMenu={canManageMenu}
            items={items}
            onEdit={openEditItem}
            onDelete={openDeleteItem}
            formatPrice={formatPrice}
          />
        );
      }

      return (
        <MenuItemsTable
          canManageMenu={canManageMenu}
          items={items}
          selectedOutletName={selectedOutlet?.name || null}
          onEdit={openEditItem}
          onDelete={openDeleteItem}
          formatPrice={formatPrice}
        />
      );
    };

    return (
      <>
        {renderContent()}

        {pagination && items.length > 0 && (
          <PaginationControls
            pagination={pagination}
            itemLabel="items"
            onPageChange={onPageChange}
          />
        )}

        <MenuItemFormModal
          isOpen={isItemModalOpen}
          onClose={() => {
            setIsItemModalOpen(false);
            setSelectedItem(null);
          }}
          onSubmit={selectedItem ? handleUpdateItem : handleCreateItem}
          item={selectedItem}
          categories={categories}
          departments={departments}
          isLoading={isSubmitting}
        />
        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedItem(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Delete Menu Item"
          message={`Are you sure you want to delete "${selectedItem?.name}"?`}
          isLoading={isSubmitting}
        />
      </>
    );
  },
);

MenuItems.displayName = 'MenuItems';
